import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useLazyQuery, useMutation, ApolloError } from '@apollo/client';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import LoadingButton from '@mui/lab/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import { useAuth } from '@calefy-inc/authentication';
import { Form } from '@calefy-inc/informedMaterial';
import { withFormStyles, WithFormStyles } from '../../util/withFormStyles';
import { FINAL_FORM } from '../../queries/queries/finalForm';
import { DELETE_FORM } from '../../queries';
import { useAllowByPermission } from '../../hooks';
import { QuoteWizardQuestionInstance } from '../QuoteWizard/classes';
import { Language } from '../../Typescript/classes/Language';
import { ProgramBuilderQuestionInstance } from '../FormManager/classes';
import RenderedQuestionInstance from '../common/QuoteComponents/RenderedQuestionInstance';
import { DisplayInlineError } from '../common/ErrorHandling';
import { useSnackbar } from 'notistack';
import type { FinalFormQuery } from '../../gql/graphql';

interface AdminPanelDisplayFormDialogProps extends WithFormStyles {
  open: boolean;
  onClose: () => void;
  formId: string | null;
  formTitle?: string;
  onFormDelete?: (formId: string) => void;
}

type QuestionInstanceType = NonNullable<
  NonNullable<FinalFormQuery['finalForm']>['questionInstances']
>[number];

export const AdminPanelDisplayFormDialog = withFormStyles()(
  ({
    open,
    onClose,
    formId,
    formTitle,
    onFormDelete,
    classes,
  }: AdminPanelDisplayFormDialogProps) => {
    const { token } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const hasDeleteFormPermissions = useAllowByPermission('delete:forms');
    const [getForm, { loading, error, data }] = useLazyQuery(FINAL_FORM);
    const [questionInstances, setQuestionInstances] = useState<
      Array<QuoteWizardQuestionInstance>
    >([]);
    const [displayTitle, setDisplayTitle] = useState<string>('Form Preview');
    const [waitingToDelete, setWaitingToDelete] = useState<boolean>(false);
    const [okToDelete, setOkToDelete] = useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
    const [
      deleteForm,
      { loading: deleteLoading, error: deleteError, data: deleteData },
    ] = useMutation(DELETE_FORM, {
      update(cache, { data }) {
        const deletedFormId = data?.deleteFinalForm?.formId;
        if (!deletedFormId) {
          return;
        }
        cache.evict({ id: `FinalFormType:${deletedFormId}` });
      },
    });

    useEffect(() => {
      if (open && formId && token) {
        getForm({
          variables: {
            id: formId,
            token,
          },
        });
      }
    }, [open, formId, token, getForm]);

    useEffect(() => {
      if (data?.finalForm?.questionInstances) {
        const converted = data.finalForm.questionInstances.map((qi) =>
          convertQuestionInstance(qi, Language.english),
        );
        setQuestionInstances(converted);
      } else {
        setQuestionInstances([]);
      }
    }, [data]);

    useEffect(() => {
      if (formTitle) {
        setDisplayTitle(formTitle);
      } else if (data?.finalForm?.internalName) {
        setDisplayTitle(data.finalForm.internalName);
      } else {
        setDisplayTitle('Form Preview');
      }
    }, [formTitle, data?.finalForm?.internalName]);

    // Delete the form from the backend when requested
    useEffect(() => {
      if (formId && okToDelete && token && hasDeleteFormPermissions === true) {
        deleteForm({
          variables: {
            formId,
            token,
          },
        });
        setWaitingToDelete(false);
        setOkToDelete(false);
      }
    }, [formId, okToDelete, token, deleteForm, hasDeleteFormPermissions]);

    // Handle successful deletion
    useEffect(() => {
      if (deleteData?.deleteFinalForm?.formId && formId) {
        enqueueSnackbar('Successfully deleted form!', { variant: 'success' });
        onFormDelete?.(formId);
        onClose();
      }
    }, [deleteData, formId, enqueueSnackbar, onFormDelete, onClose]);

    // Handle delete error
    useEffect(() => {
      if (deleteError) {
        setErrorDialogOpen(true);
        enqueueSnackbar('Error deleting form', { variant: 'error' });
        setWaitingToDelete(false);
        setOkToDelete(false);
      }
    }, [deleteError, enqueueSnackbar]);

    const formName = displayTitle;

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='md'
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='h6'>{displayTitle}</Typography>
              {hasDeleteFormPermissions === true && formId ? (
                <Tooltip title='Delete the form'>
                  <span>
                    <LoadingButton
                      variant='contained'
                      color='error'
                      size='small'
                      onClick={() => setWaitingToDelete(true)}
                      disabled={!formId}
                      loading={deleteLoading}
                      endIcon={<DeleteIcon />}
                    >
                      Delete Form
                    </LoadingButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {loading ? (
              <CircularProgress />
            ) : error ? (
              <DisplayInlineError error={error} />
            ) : questionInstances.length > 0 ? (
              <Form className={classes.dialogForm}>
                {questionInstances.map((questionInstance) => (
                  <RenderedQuestionInstance
                    key={questionInstance.id}
                    questionInstance={questionInstance}
                    classes={classes}
                  />
                ))}
              </Form>
            ) : data?.finalForm ? (
              <p>This form has no questions.</p>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Dialog>
        <DeleteFormDialog
          open={waitingToDelete && !okToDelete}
          formName={formName}
          onConfirmation={() => {
            setOkToDelete(true);
          }}
          onCancel={() => {
            setWaitingToDelete(false);
          }}
        />
        <DeleteFormErrorDialog
          error={deleteError}
          open={errorDialogOpen}
          setOpen={setErrorDialogOpen}
        />
      </>
    );
  },
);

interface DeleteFormDialogProps {
  open: boolean;
  onConfirmation: () => void;
  onCancel: () => void;
  formName: string;
}

function DeleteFormDialog({
  open,
  onConfirmation,
  onCancel,
  formName,
}: DeleteFormDialogProps) {
  const [enteredValue, setEnteredValue] = useState<string>('');
  return (
    <Dialog open={open} data-testid='delete-form-modal'>
      <DialogTitle>Are You Sure?</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          To be sure that this is really the form that you want to delete,
          please type the name of the form:
        </Typography>
        <Typography gutterBottom variant='subtitle1'>
          {formName}
        </Typography>
        <TextField
          label='Form to Delete'
          id='form-to-delete'
          fullWidth
          value={enteredValue}
          onChange={(e) => {
            setEnteredValue(e.target.value);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel()}>Cancel</Button>
        <Button
          onClick={() => onConfirmation()}
          disabled={enteredValue !== formName}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DeleteFormErrorDialogProps {
  error?: ApolloError;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function DeleteFormErrorDialog({
  error,
  open,
  setOpen,
}: DeleteFormErrorDialogProps) {
  return (
    <Dialog open={open} data-testid='form-delete-error'>
      <DialogTitle>Error Deleting Form</DialogTitle>
      <DialogContent>
        <Typography>
          There was an error deleting the form. Please contact{' '}
          <a href='mailto:support@calefy.ca?subject=Error when attempting to delete form'>
            support@calefy.ca
          </a>{' '}
          with details about which form you were trying to delete and any other
          information you can think of.
        </Typography>
        {error ? (
          <>
            <Typography>The returned error was</Typography>
            <pre>{error.message}</pre>
          </>
        ) : null}
        <Typography>Our apologies for the inconvenience.</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={() => setOpen(false)}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Convert GraphQL QuestionInstanceType to QuoteWizardQuestionInstance
 * Uses the same approach as QuoteWizard: first convert to ProgramBuilderQuestionInstance,
 * then to QuoteWizardQuestionInstance with the specified language
 */
function convertQuestionInstance(
  questionInstance: QuestionInstanceType,
  language: Language = Language.english,
): QuoteWizardQuestionInstance {
  const programBuilderQuestionInstance =
    ProgramBuilderQuestionInstance.generateFromBackendResponse(
      questionInstance,
    );
  return programBuilderQuestionInstance.toQuoteWizardQuestionInstance(language);
}
